import React from "react";
import { motion } from "framer-motion";
import { WindsongText, NoticiaText } from "@/components/Typography";
import Image from "next/image";
import linkImage from "@/utils/linkImage";

interface HeroSectionProps {
  headlineScript: string;
  headlineLine1: string;
  headlineLine2: string;

  backgroundImage?: string;
}

export const HeroSection = ({
  headlineScript,
  headlineLine1,
  headlineLine2,
  backgroundImage,
}: HeroSectionProps) => {
  const isLongScript = (headlineScript || "").length > 3;

  // Responsive font sizes for the script text
  const scriptPaddingClass = isLongScript
    ? "px-2 py-3 sm:px-3 sm:py-4 md:px-4 md:py-6"
    : "px-4 py-3 sm:px-6 sm:py-4 md:px-8 md:py-6";

  // Margins for right stacked text wrapper to control the overlap
  const rightContainerClass = isLongScript
    ? "-ml-4 sm:-ml-6 md:-ml-10 lg:-ml-12"
    : "-ml-7 sm:-ml-12 md:-ml-[72px] lg:-ml-[95px]";

  const headline1Ml = isLongScript
    ? "ml-[10px] sm:ml-[15px] md:ml-[20px] lg:ml-[25px]"
    : "ml-[20px] sm:ml-[35px] md:ml-[50px] lg:ml-[60px]";

  const headline2Ml = isLongScript
    ? "ml-[2px] sm:ml-[4px] md:ml-[5px] lg:ml-[8px]"
    : "ml-[5px] sm:ml-[10px] md:ml-[12px] lg:ml-[15px]";

  return (
    <section
      className="relative h-[65vh] min-h-[460px] lg:h-auto lg:min-h-0 w-full flex flex-col justify-between z-10 overflow-hidden"
      style={{ aspectRatio: "1919 / 988" }}
    >
      {/* Background Image & Overlay */}
      <div className="absolute inset-0 z-0 saturate-[1.2]">
        <Image
          src={linkImage(backgroundImage || "/images/hero.png")}
          alt="Eco-Industrial Park Masterplan"
          fill
          priority
          sizes="100vw"
          className="object-cover brightness-[1.05] contrast-[1.12] saturate-[1.08]"
          style={{ imageRendering: "-webkit-optimize-contrast" }}
        />
        {/* Subtle Bright Teal Overlay (Reduced to 5% for maximum image clarity) */}
        <div className="absolute inset-0 bg-[#06191e]/35" />
        {/* Top Dark Shadow Overlay for Header Readability */}
        <div className="absolute top-0 left-0 right-0 h-44 bg-gradient-to-b from-[#0d3f4d]/60 via-[#06191e]/15 to-transparent pointer-events-none" />
        {/* Edge fading effects */}
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-[#0d3f4d]/10 to-[#0d3f4d] pointer-events-none" />
      </div>

      {/* Hero Content (Headline & Description - Positioned right at the bottom) */}
      <div className="relative z-20 flex-1 flex flex-col justify-end items-center px-6 md:px-12 pt-8 pb-0 sm:pb-0 md:pb-0 lg:pb-0">
        <div className="w-full  flex flex-col items-center text-center">
          {/* Main Headline */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.2 }}
            className="relative flex justify-center select-none w-full mt-8"
          >
            <div className="flex flex-row items-center justify-center -translate-x-6 sm:translate-x-0">
              {/* Large Script "Mở" on the left */}
              <WindsongText
                sizes={
                  isLongScript
                    ? {
                        mobile: "38px",
                        mobileLg: "60px",
                        tablet: "90px",
                        laptop: "110px",
                        desktop: "110px",
                      }
                    : {
                        mobile: "70px",
                        mobileLg: "110px",
                        tablet: "170px",
                        laptop: "200px",
                        desktop: "200px",
                      }
                }
                className={`relative shrink-0 inline-block font-medium text-transparent bg-clip-text leading-[1.1] select-none filter drop-shadow-[0_4px_12px_rgba(13,63,77,0.3)] z-10 capitalize ${scriptPaddingClass}`}
                style={{
                  background: "linear-gradient(to right,  #FFF0B8, #DBB653)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  transform: isLongScript ? "rotate(-10deg)" : "rotate(-15deg)",
                }}
              >
                {headlineScript}
              </WindsongText>

              {/* Right stacked texts */}
              <div
                className={`relative shrink-0 flex flex-col items-start text-left z-20 ${rightContainerClass}`}
              >
                <NoticiaText
                  as="span"
                  variant="heroTitle"
                  className={`whitespace-nowrap font-normal ${headline1Ml} lg:-mt-[5px] text-white tracking-widest leading-none drop-shadow-md select-none uppercase`}
                >
                  {headlineLine1}
                </NoticiaText>
                <NoticiaText
                  as="span"
                  variant="heroTitle"
                  className={`whitespace-nowrap font-normal ${headline2Ml} text-white tracking-widest leading-none mt-1 md:mt-2 drop-shadow-md select-none uppercase`}
                >
                  {headlineLine2}
                </NoticiaText>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
